import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Brain, Loader2, BarChart3, FileText, Shield, Sparkles,
  Download, ChevronRight, ArrowLeft, Send, RotateCcw,
  Users, Target, Heart, Award, AlertTriangle, TrendingUp,
  Zap, MapPin, GraduationCap, Briefcase, Lock, Crown,
  ChevronDown, ChevronUp, Share2, Info, Eye, Lightbulb,
  DollarSign, Calendar, ArrowUpRight, MessageCircle,
  CheckCircle, AlertCircle, Compass, Star, Linkedin
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell, PieChart, Pie, AreaChart, Area, LineChart, Line,
  Legend
} from 'recharts';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

const TABS = [
  { id: 'report-card', label: 'Grade Report', icon: BarChart3 },
  { id: 'persona', label: 'Career Persona', icon: Users },
  { id: 'analysis', label: 'Career Analysis', icon: FileText },
  { id: 'panel', label: 'Expert Panel', icon: Crown },
  { id: 'devils-advocate', label: "Devil's Advocate", icon: Shield },
];

const DIM_ICONS = {
  psychological_fitness: Brain, emotional_intelligence: Heart,
  behavioral_alignment: Target, technical_competency: GraduationCap,
  leadership_readiness: Crown, analytical_thinking: Brain,
  strategic_thinking: Brain, growth_trajectory: TrendingUp,
  market_alignment: Briefcase, market_position: Briefcase,
  political_social_capital: Users, adaptability: Zap,
  geographic_mobility: MapPin, risk_profile: AlertTriangle,
  network_visibility: Share2, credential_strength: Award,
  career_trajectory: TrendingUp, linkedin_presence: Linkedin,
};

const PERSONA_ICONS = {
  strategic_architect: Compass, people_champion: Heart,
  risk_navigator: Shield, innovation_catalyst: Lightbulb,
  execution_engine: Zap, growth_seeker: TrendingUp,
  diplomatic_operator: Users, market_strategist: Target,
};

function gradeColor(grade) {
  if (!grade || grade === 'N/A') return '#6B7280';
  if (grade.startsWith('A')) return '#16A34A';
  if (grade.startsWith('B')) return '#0D9488';
  if (grade.startsWith('C')) return '#F5841F';
  return '#DC2626';
}

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.35 },
});

async function shareAsImage(ref, filename = 'kairera-report') {
  if (!ref.current) return;
  try {
    const canvas = await html2canvas(ref.current, {
      backgroundColor: '#111827', scale: 2, useCORS: true,
    });
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Downloaded successfully');
  } catch { toast.error('Download failed'); }
}

// ===========================================================
// Tab: Grade Report Card (unchanged, kept as-is)
// ===========================================================
function ReportCardTab({ data, loading }) {
  if (loading) return <LoadingState text="Loading grades..." />;
  if (!data) return <EmptyState text="Complete assessments to unlock your report card." />;

  const radarData = data.dimensions
    .filter(d => d.score > 0)
    .map(d => ({ name: d.name.split(' ').slice(0, 2).join(' '), score: d.score, fullMark: 10 }));

  const handleExportPDF = () => {
    const url = kaireraAPI.exportReportPDF();
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="report-card-tab">
      <div className="rounded-xl p-6" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
            Overall Career Intelligence
          </p>
          <Button onClick={handleExportPDF} className="h-8 px-3 text-xs rounded-lg gap-1.5"
            style={{ background: '#1B3461', color: '#FFFFFF' }} data-testid="export-pdf-btn">
            <Download className="w-3.5 h-3.5" /> Export PDF
          </Button>
        </div>
        <div className="flex items-center justify-center gap-4">
          <div className="text-5xl font-black" style={{ color: gradeColor(data.overall_grade) }}>
            {data.overall_grade}
          </div>
          <div className="text-left">
            <p className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>{data.overall_score}/10</p>
            <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
              {data.graded_count}/{data.total_dimensions} dimensions graded
            </p>
          </div>
        </div>
      </div>

      {radarData.length >= 3 && (
        <div className="nx-card p-4" style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--t-border)" />
              <PolarAngleAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 10 }} />
              <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
              <Radar name="Score" dataKey="score" stroke="#0D9488" fill="#0D9488" fillOpacity={0.15} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.dimensions.map((dim, i) => {
          const Icon = DIM_ICONS[dim.id] || Brain;
          const color = dim.score > 0 ? gradeColor(dim.grade) : '#6B7280';
          return (
            <motion.div key={dim.id} {...fadeIn(i * 0.04)}
              className="nx-card p-4 flex items-center gap-3" data-testid={`dim-${dim.id}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${color}12` }}>
                {dim.score > 0 ? <Icon className="w-5 h-5" style={{ color }} />
                  : <Lock className="w-4 h-4" style={{ color: '#6B7280' }} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{dim.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--t-border)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${(dim.score / 10) * 100}%`, background: color }} />
                  </div>
                  <span className="text-[10px] font-bold" style={{ color }}>{dim.score > 0 ? dim.score : '-'}</span>
                </div>
              </div>
              <div className="text-lg font-black" style={{ color }}>{dim.grade}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================
// Tab: Career Persona (Enhanced)
// ===========================================================
function PersonaTab({ data, loading }) {
  const personaRef = useRef(null);
  const [showMethod, setShowMethod] = useState(false);

  if (loading) return <LoadingState text="Classifying your career persona..." />;
  if (!data || data.error) return <EmptyState text="Complete more assessments to unlock persona classification." />;

  const primary = data.primary;
  const secondary = data.secondary;
  const personas = data.personas || [];
  const PrimaryIcon = PERSONA_ICONS[primary.id] || Star;
  const SecondaryIcon = PERSONA_ICONS[secondary.id] || Star;

  const barData = personas.slice(0, 6).map(p => ({
    name: p.name.split(' ')[0],
    affinity: p.affinity,
    color: p.color,
    full: p.name,
  }));

  return (
    <div className="space-y-6" data-testid="persona-tab" ref={personaRef}>
      {/* Download/Share bar */}
      <div className="flex justify-end gap-2">
        <Button onClick={() => shareAsImage(personaRef, 'career-persona')}
          className="h-8 px-3 text-xs rounded-lg gap-1.5"
          style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
          data-testid="download-persona-btn">
          <Download className="w-3.5 h-3.5" /> Download
        </Button>
      </div>

      {/* Primary Persona Hero */}
      <motion.div {...fadeIn(0)}
        className="relative rounded-2xl p-6 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${primary.color}10, ${primary.color}05)`, border: `2px solid ${primary.color}25` }}>
        <div className="absolute top-4 right-4 opacity-[0.04]">
          <PrimaryIcon className="w-32 h-32" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${primary.color}18` }}>
              <PrimaryIcon className="w-5 h-5" style={{ color: primary.color }} />
            </div>
            <div>
              <span className="text-[9px] font-bold tracking-[0.25em] uppercase" style={{ color: '#F5841F' }}>
                Primary Persona
              </span>
              <h2 className="text-xl font-black" style={{ color: primary.color }}>{primary.name}</h2>
            </div>
          </div>

          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--t-text-secondary)' }}>{primary.desc}</p>

          {/* Affinity Gauge */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1">
              <div className="flex justify-between mb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--t-text-muted)' }}>Affinity Match</span>
                <span className="text-sm font-black" style={{ color: primary.color }}>{primary.affinity}%</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${primary.affinity}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${primary.color}, ${primary.color}aa)` }} />
              </div>
            </div>
          </div>

          {/* Key Strengths */}
          {primary.strong && (
            <div className="flex flex-wrap gap-2 mb-4">
              {primary.strong.map(s => (
                <span key={s} className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: `${primary.color}10`, color: primary.color, border: `1px solid ${primary.color}25` }}>
                  {s.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}

          {/* AI Narrative */}
          {primary.narrative && (
            <div className="p-4 rounded-xl" style={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#0D9488' }}>
                  Expert Insight
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{primary.narrative}</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Secondary Persona */}
      <motion.div {...fadeIn(0.1)} className="nx-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: `${secondary.color}12` }}>
            <SecondaryIcon className="w-4 h-4" style={{ color: secondary.color }} />
          </div>
          <div className="flex-1">
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase" style={{ color: 'var(--t-text-muted)' }}>
              Secondary Persona
            </span>
            <h3 className="text-base font-bold" style={{ color: secondary.color }}>{secondary.name}</h3>
          </div>
          <span className="text-lg font-black" style={{ color: secondary.color }}>{secondary.affinity}%</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>{secondary.desc}</p>
        <div className="h-2 rounded-full overflow-hidden mt-3" style={{ background: 'var(--t-border)' }}>
          <div className="h-full rounded-full" style={{ width: `${secondary.affinity}%`, background: secondary.color }} />
        </div>
      </motion.div>

      {/* Affinity Chart */}
      <motion.div {...fadeIn(0.15)} className="nx-card p-5">
        <h3 className="text-xs font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>
          Persona Affinity Distribution
        </h3>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--t-text-muted)', fontSize: 10 }}
                tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" width={85}
                tick={{ fill: 'var(--t-text-muted)', fontSize: 11, fontWeight: 600 }} />
              <Tooltip
                formatter={(v, n, p) => [`${v}%`, p.payload.full]}
                contentStyle={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'var(--t-text-heading)', fontWeight: 700 }} />
              <Bar dataKey="affinity" radius={[0, 6, 6, 0]} barSize={18}>
                {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* How We Calculated */}
      <motion.div {...fadeIn(0.2)}>
        <button onClick={() => setShowMethod(!showMethod)}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-xl text-left transition-all"
          style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}
          data-testid="toggle-method-btn">
          <Info className="w-4 h-4" style={{ color: '#0D9488' }} />
          <span className="text-xs font-bold flex-1" style={{ color: 'var(--t-text-heading)' }}>
            How We Calculated Your Persona
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showMethod ? 'rotate-180' : ''}`}
            style={{ color: 'var(--t-text-muted)' }} />
        </button>
        <AnimatePresence>
          {showMethod && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="px-4 py-3 text-xs leading-relaxed space-y-2"
                style={{ color: 'var(--t-text-muted)', background: 'var(--t-bg-section)', borderRadius: '0 0 12px 12px', border: '1px solid var(--t-border)', borderTop: 'none' }}>
                <p>Your persona is classified using 8 career archetypes mapped against your 13 Career Intelligence dimensions.</p>
                <p><strong style={{ color: 'var(--t-text-heading)' }}>Affinity Score</strong> = Average of your grades in the persona's key dimensions, expressed as a percentage of the maximum possible score (10/10).</p>
                <p>The persona with the highest affinity becomes your Primary, the second-highest becomes your Secondary. The combination of both reveals your unique career DNA.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// ===========================================================
// Tab: Career Analysis (Enhanced with visuals)
// ===========================================================
function AnalysisTab({ data, loading, reportCardData }) {
  const [activeSection, setActiveSection] = useState(null);
  const [marketDemand, setMarketDemand] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [peerBenchmark, setPeerBenchmark] = useState(null);
  const [peerLoading, setPeerLoading] = useState(false);
  const analysisRef = useRef(null);

  if (loading) return <LoadingState text="Generating your career analysis... This may take a moment." />;
  if (!data?.report) return <EmptyState text="Complete assessments to unlock your career analysis report." />;

  const sections = data.report.sections || [];
  // Add peer_benchmark as a virtual section
  const allTabs = [...sections, { id: 'peer_benchmark', title: 'Peer Benchmark' }];

  if (!activeSection && allTabs.length > 0) {
    setTimeout(() => setActiveSection(allTabs[0].id), 0);
  }

  const SECTION_META = {
    executive_summary: { icon: FileText, color: '#0D9488', label: 'Executive Summary' },
    strengths_superpowers: { icon: Zap, color: '#16A34A', label: 'Strengths' },
    development_risks: { icon: AlertTriangle, color: '#DC2626', label: 'Risks' },
    career_paths: { icon: Compass, color: '#8B5CF6', label: 'Career Paths' },
    action_plan: { icon: Calendar, color: '#F5841F', label: '90-Day Plan' },
    market_intelligence: { icon: TrendingUp, color: '#3B82F6', label: 'Market Intel' },
    peer_benchmark: { icon: Users, color: '#EC4899', label: 'Peer Benchmark' },
  };

  const currentSection = sections.find(s => s.id === activeSection);
  const currentMeta = SECTION_META[activeSection] || {};

  // Load market demand data when Market Intel tab selected
  const loadMarketDemand = async () => {
    if (marketDemand || marketLoading) return;
    setMarketLoading(true);
    try {
      const res = await kaireraAPI.getMarketDemand();
      setMarketDemand(res.data.market_demand);
    } catch { /* silent */ }
    finally { setMarketLoading(false); }
  };

  // Load peer benchmark data when Peer Benchmark tab selected
  const loadPeerBenchmark = async () => {
    if (peerBenchmark || peerLoading) return;
    setPeerLoading(true);
    try {
      const res = await kaireraAPI.getPeerBenchmark();
      setPeerBenchmark(res.data.benchmark);
    } catch { /* silent */ }
    finally { setPeerLoading(false); }
  };

  if (activeSection === 'market_intelligence' && !marketDemand && !marketLoading) {
    loadMarketDemand();
  }
  if (activeSection === 'peer_benchmark' && !peerBenchmark && !peerLoading) {
    loadPeerBenchmark();
  }

  return (
    <div className="space-y-5" data-testid="analysis-tab" ref={analysisRef}>
      {/* Header with download */}
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
          Deep-dive intelligence across {sections.length} dimensions of your career.
        </p>
        <Button onClick={() => shareAsImage(analysisRef, 'career-analysis')}
          className="h-8 px-3 text-xs rounded-lg gap-1.5 flex-shrink-0"
          style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
          data-testid="download-analysis-btn">
          <Download className="w-3.5 h-3.5" /> Download
        </Button>
      </div>

      {/* Section Tabs - Pill style */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {allTabs.map((sec) => {
          const meta = SECTION_META[sec.id] || {};
          const SIcon = meta.icon || FileText;
          const isActive = activeSection === sec.id;
          return (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl whitespace-nowrap flex-shrink-0"
              style={{
                background: isActive ? meta.color : 'var(--t-bg-section)',
                color: isActive ? '#fff' : 'var(--t-text-muted)',
                border: `1.5px solid ${isActive ? meta.color : 'var(--t-border)'}`,
                boxShadow: isActive ? `0 4px 16px ${meta.color}30` : 'none',
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                transform: isActive ? 'scale(1.03)' : 'scale(1)',
              }}
              data-testid={`analysis-tab-${sec.id}`}>
              <SIcon className="w-3.5 h-3.5" />
              <span className="text-xs font-bold">{meta.label || sec.title}</span>
            </button>
          );
        })}
      </div>

      {/* Active Section Content */}
      <AnimatePresence mode="wait">
        {(currentSection || activeSection === 'peer_benchmark') && (
          <motion.div key={activeSection}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3, ease: 'easeOut' }}
            data-testid={`analysis-content-${activeSection}`}>

            {activeSection === 'executive_summary' && (
              <ExecutiveSummaryView content={currentSection.content} meta={currentMeta} grades={reportCardData} />
            )}
            {activeSection === 'strengths_superpowers' && (
              <StrengthsView content={currentSection.content} meta={currentMeta} grades={reportCardData} />
            )}
            {activeSection === 'development_risks' && (
              <RisksView content={currentSection.content} meta={currentMeta} grades={reportCardData} />
            )}
            {activeSection === 'career_paths' && (
              <CareerPathsView content={currentSection.content} meta={currentMeta} />
            )}
            {activeSection === 'action_plan' && (
              <ActionPlanView content={currentSection.content} meta={currentMeta} />
            )}
            {activeSection === 'market_intelligence' && (
              <MarketIntelView content={currentSection.content} meta={currentMeta} marketDemand={marketDemand} marketLoading={marketLoading} />
            )}
            {activeSection === 'peer_benchmark' && (
              <PeerBenchmarkView benchmark={peerBenchmark} loading={peerLoading} meta={currentMeta} grades={reportCardData} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Methodology Footer */}
      <div className="rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
          <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: '#0D9488' }}>
            How This Report Was Generated
          </span>
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
          This analysis synthesizes your 13 Career Intelligence dimensions, discovery answers,
          assessment battery results, and real-time market data. Each section is generated using expert-level
          career intelligence methodology, calibrated to your target geography and industry.
        </p>
      </div>
    </div>
  );
}

// --- Analysis Sub-Views ---

function AnimatedCounter({ value, suffix = '', prefix = '', delay = 0 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return;
    const timer = setTimeout(() => {
      const duration = 1200;
      const start = performance.now();
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * num * 10) / 10);
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return <>{prefix}{Number.isInteger(count) ? count : count.toFixed(1)}{suffix}</>;
}

function ExecutiveSummaryView({ content, meta, grades }) {
  const paragraphs = content.split('\n\n').filter(p => p.trim() && !p.startsWith('##'));
  const dims = grades?.dimensions?.filter(d => d.score > 0) || [];
  const overallScore = grades?.overall_score || 0;
  const overallGrade = grades?.overall_grade || 'N/A';

  // Radar data
  const radarData = dims.map(d => ({
    dimension: d.name.split(' ').slice(0, 2).join(' '),
    score: d.score,
    fullMark: 10,
  }));

  // Score distribution data for area chart
  const distributionData = [...dims]
    .sort((a, b) => b.score - a.score)
    .map((d, i) => ({
      name: d.name.split(' ')[0],
      score: d.score,
      benchmark: 7,
    }));

  const scorePercent = (overallScore / 10) * 100;

  return (
    <div className="space-y-5">
      {/* Hero Score Card */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="nx-card overflow-hidden" data-testid="exec-summary-hero">
        <div className="p-6" style={{ background: `linear-gradient(135deg, ${meta.color}08, ${meta.color}15)` }}>
          <div className="grid grid-cols-3 gap-4 items-center">
            {/* Score Gauge */}
            <div className="text-center">
              <div className="relative w-28 h-28 mx-auto">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="var(--t-border)" strokeWidth="6" />
                  <motion.circle cx="50" cy="50" r="42" fill="none"
                    stroke={meta.color} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - scorePercent / 100) }}
                    transition={{ duration: 1.5, delay: 0.3, ease: 'easeOut' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black" style={{ color: meta.color }}>
                    <AnimatedCounter value={overallScore} suffix="" delay={300} />
                  </span>
                  <span className="text-[9px] font-bold" style={{ color: 'var(--t-text-muted)' }}>out of 10</span>
                </div>
              </div>
              <p className="text-[10px] font-bold mt-2 uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>
                Career Score
              </p>
            </div>

            {/* Grade + Stats */}
            <div className="text-center space-y-3">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}>
                <span className="text-5xl font-black" style={{ color: gradeColor(overallGrade) }}>
                  {overallGrade}
                </span>
              </motion.div>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>
                Overall Grade
              </p>
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#16A34A' }} />
                <span className="text-[10px] font-semibold" style={{ color: '#16A34A' }}>
                  {grades?.graded_count || 0}/{grades?.total_dimensions || 13} Assessed
                </span>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="space-y-2">
              {[
                { label: 'Strongest', value: dims.length > 0 ? [...dims].sort((a, b) => b.score - a.score)[0]?.name?.split(' ').slice(0,2).join(' ') : 'N/A', color: '#16A34A' },
                { label: 'Focus Area', value: dims.length > 0 ? [...dims].filter(d => d.score > 0).sort((a, b) => a.score - b.score)[0]?.name?.split(' ').slice(0,2).join(' ') : 'N/A', color: '#DC2626' },
                { label: 'Avg Score', value: dims.length > 0 ? (dims.reduce((s, d) => s + d.score, 0) / dims.length).toFixed(1) : '0', color: '#8B5CF6' },
              ].map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <span className="text-[9px] font-semibold uppercase" style={{ color: 'var(--t-text-muted)' }}>{s.label}</span>
                  <span className="text-[10px] font-bold" style={{ color: s.color }}>{s.value}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Radar + Dimension Bars Side by Side */}
      {dims.length >= 5 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Radar Chart */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="nx-card p-5" data-testid="exec-radar-chart">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: meta.color }}>
              Dimension Radar
            </p>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                  <PolarGrid stroke="var(--t-border)" />
                  <PolarAngleAxis dataKey="dimension"
                    tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]}
                    tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
                  <Radar name="Score" dataKey="score" stroke={meta.color}
                    fill={meta.color} fillOpacity={0.2} strokeWidth={2}
                    animationDuration={1500} animationEasing="ease-out" />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Score Distribution Area Chart */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="nx-card p-5" data-testid="exec-area-chart">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#8B5CF6' }}>
              Score Distribution vs Benchmark
            </p>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={distributionData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={meta.color} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={meta.color} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <Tooltip contentStyle={{
                    background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                    borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  }} />
                  <Area type="monotone" dataKey="benchmark" stroke="#6B728088"
                    fill="none" strokeDasharray="5 5" strokeWidth={1.5}
                    animationDuration={1000} />
                  <Area type="monotone" dataKey="score" stroke={meta.color}
                    fill="url(#scoreGrad)" strokeWidth={2.5}
                    animationDuration={1500} animationEasing="ease-out"
                    dot={{ fill: meta.color, r: 4, stroke: '#fff', strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      )}

      {/* Animated Dimension Bars */}
      {dims.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="nx-card p-5" data-testid="exec-dimension-bars">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: meta.color }}>
            Full Dimension Breakdown
          </p>
          <div className="space-y-2.5">
            {[...dims].sort((a, b) => b.score - a.score).map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + i * 0.04 }}
                className="flex items-center gap-3 group">
                <span className="text-[10px] font-semibold w-28 truncate text-right" style={{ color: 'var(--t-text-muted)' }}>
                  {d.name}
                </span>
                <div className="flex-1 h-6 rounded-lg overflow-hidden relative" style={{ background: 'var(--t-border)' }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${(d.score / 10) * 100}%` }}
                    transition={{ delay: 0.8 + i * 0.05, duration: 0.8, ease: 'easeOut' }}
                    className="h-full rounded-lg relative overflow-hidden"
                    style={{ background: `linear-gradient(90deg, ${gradeColor(d.grade)}bb, ${gradeColor(d.grade)})` }}>
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 opacity-30"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        animation: 'shimmer 2s ease-in-out infinite',
                      }} />
                  </motion.div>
                  <span className="absolute right-2 top-1 text-[10px] font-bold" style={{ color: 'var(--t-text-heading)' }}>
                    {d.score}/10
                  </span>
                </div>
                <motion.span
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: 1 + i * 0.05, type: 'spring' }}
                  className="text-xs font-black w-8 text-center px-1 py-0.5 rounded-md"
                  style={{ color: gradeColor(d.grade), background: `${gradeColor(d.grade)}10` }}>
                  {d.grade}
                </motion.span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Analysis Content */}
      <div className="nx-card p-5" data-testid="exec-analysis-text">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4" style={{ color: meta.color }} />
          <span className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>Analysis</span>
        </div>
        <div className="space-y-3">
          {paragraphs.map((p, i) => (
            <motion.p key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
              {p.replace(/\*\*/g, '')}
            </motion.p>
          ))}
        </div>
      </div>
    </div>
  );
}

function StrengthsView({ content, meta, grades }) {
  const blocks = content.split(/\*\*([^*]+)\*\*/g).filter(s => s.trim());
  const strengths = [];
  for (let i = 0; i < blocks.length; i += 2) {
    if (i + 1 < blocks.length) {
      strengths.push({ title: blocks[i].replace(/^#+\s*/, '').trim(), body: blocks[i + 1].trim() });
    }
  }
  if (strengths.length === 0) {
    content.split('\n\n').filter(p => p.trim() && !p.startsWith('##')).forEach((p, i) => {
      strengths.push({ title: `Strength ${i + 1}`, body: p.replace(/\*\*/g, '') });
    });
  }

  const topDims = grades?.dimensions?.filter(d => d.score >= 8).sort((a, b) => b.score - a.score) || [];
  const allDims = grades?.dimensions?.filter(d => d.score > 0).sort((a, b) => b.score - a.score) || [];

  const STRENGTH_COLORS = ['#16A34A', '#0D9488', '#8B5CF6', '#3B82F6', '#EC4899', '#F5841F'];

  // Bar chart data for top dimensions
  const barData = allDims.slice(0, 8).map(d => ({
    name: d.name.split(' ')[0],
    score: d.score,
    fill: d.score >= 8 ? '#16A34A' : d.score >= 7 ? '#0D9488' : '#F5841F',
  }));

  return (
    <div className="space-y-5">
      {/* Top Scores Podium - Visual bars */}
      {topDims.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="nx-card p-5" data-testid="strengths-podium">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: meta.color }}>
            Your Power Dimensions
          </p>
          <div className="flex items-end justify-center gap-3" style={{ height: 180 }}>
            {topDims.slice(0, 5).map((d, i) => {
              const height = 50 + (d.score / 10) * 120;
              const color = STRENGTH_COLORS[i % STRENGTH_COLORS.length];
              return (
                <motion.div key={d.id}
                  initial={{ height: 0, opacity: 0 }} animate={{ height, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
                  className="w-16 rounded-t-xl flex flex-col items-center justify-start pt-3 relative overflow-hidden cursor-pointer group"
                  style={{ background: `linear-gradient(180deg, ${color}, ${color}88)` }}
                  data-testid={`strength-bar-${d.id}`}>
                  {/* Pulse ring on top */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: color, animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
                  <span className="text-white text-xl font-black drop-shadow-lg">{d.grade}</span>
                  <span className="text-white/80 text-[10px] font-bold">{d.score}/10</span>
                </motion.div>
              );
            })}
          </div>
          <div className="flex justify-center gap-3 mt-3">
            {topDims.slice(0, 5).map((d, i) => (
              <span key={d.id} className="text-[8px] font-bold text-center w-16 truncate"
                style={{ color: STRENGTH_COLORS[i % STRENGTH_COLORS.length] }}>
                {d.name.split(' ').slice(0,2).join(' ')}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Comparative Bar Chart */}
      {barData.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="nx-card p-5" data-testid="strengths-bar-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#8B5CF6' }}>
            Dimension Strength Map
          </p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <defs>
                  {STRENGTH_COLORS.map((c, i) => (
                    <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={1} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }} formatter={(v) => [`${v}/10`, 'Score']} />
                <Bar dataKey="score" radius={[8, 8, 0, 0]} barSize={32}
                  animationDuration={1200} animationEasing="ease-out">
                  {barData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Strength Cards */}
      <div className="grid gap-4">
        {strengths.slice(0, 6).map((s, i) => {
          const color = STRENGTH_COLORS[i % STRENGTH_COLORS.length];
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="nx-card p-5 overflow-hidden relative"
              style={{ borderLeft: `4px solid ${color}` }}
              data-testid={`strength-card-${i}`}>
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full"
                style={{ background: `${color}06`, transform: 'translate(30%, -30%)' }} />
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: `${color}12` }}>
                  <Zap className="w-4 h-4" style={{ color }} />
                </div>
                <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>
                  {s.title}
                </h3>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                {s.body.replace(/\*\*/g, '').split('\n')[0]}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function RisksView({ content, meta, grades }) {
  const blocks = content.split(/\*\*\d+\.\s*([^*]+)\*\*/g).filter(s => s.trim());
  const risks = [];
  for (let i = 0; i < blocks.length; i += 2) {
    if (i + 1 < blocks.length) {
      risks.push({ title: blocks[i].trim(), body: blocks[i + 1].trim() });
    }
  }
  if (risks.length === 0) {
    content.split('\n\n').filter(p => p.trim() && !p.startsWith('**Development')).forEach((p, i) => {
      risks.push({ title: `Area ${i + 1}`, body: p.replace(/\*\*/g, '') });
    });
  }

  const weakDims = grades?.dimensions?.filter(d => d.score > 0 && d.score < 7).sort((a, b) => a.score - b.score) || [];
  const allDims = grades?.dimensions?.filter(d => d.score > 0) || [];

  const RISK_LEVELS = ['#DC2626', '#F97316', '#EAB308'];

  // Build risk gap chart data
  const gapData = allDims.map(d => ({
    name: d.name.split(' ')[0],
    gap: Math.max(0, 10 - d.score),
    score: d.score,
  })).sort((a, b) => b.gap - a.gap).slice(0, 8);

  return (
    <div className="space-y-5">
      {/* Risk Severity Overview */}
      {weakDims.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="nx-card p-5" data-testid="risks-severity">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: meta.color }}>
            Dimensions Needing Attention
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {weakDims.map((d, i) => {
              const severity = d.score < 5 ? 0 : d.score < 6 ? 1 : 2;
              const riskColor = RISK_LEVELS[severity];
              const riskLabel = severity === 0 ? 'Critical' : severity === 1 ? 'Warning' : 'Watch';
              return (
                <motion.div key={d.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 rounded-xl text-center"
                  style={{ background: `${riskColor}08`, border: `1.5px solid ${riskColor}25` }}
                  data-testid={`risk-dim-${d.id}`}>
                  <div className="relative w-14 h-14 mx-auto mb-2">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="38" fill="none" stroke={`${riskColor}20`} strokeWidth="8" />
                      <motion.circle cx="50" cy="50" r="38" fill="none"
                        stroke={riskColor} strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 38}`}
                        initial={{ strokeDashoffset: 2 * Math.PI * 38 }}
                        animate={{ strokeDashoffset: 2 * Math.PI * 38 * (1 - d.score / 10) }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.15 }} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-black" style={{ color: riskColor }}>{d.score}</span>
                    </div>
                  </div>
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{d.name}</p>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full mt-1 inline-block"
                    style={{ background: `${riskColor}15`, color: riskColor }}>
                    {riskLabel}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Gap Analysis Chart */}
      {gapData.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="nx-card p-5" data-testid="risks-gap-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#DC2626' }}>
            Gap Analysis (Room for Growth)
          </p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gapData} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="gapGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#DC2626" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#F97316" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" horizontal={false} />
                <XAxis type="number" domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis type="category" dataKey="name" width={70}
                  tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }} formatter={(v, name) => [name === 'gap' ? `${v} points to grow` : `${v}/10`, name === 'gap' ? 'Growth Gap' : 'Current Score']} />
                <Bar dataKey="score" stackId="a" fill="#0D948830" barSize={20} radius={0}
                  animationDuration={1000} />
                <Bar dataKey="gap" stackId="a" fill="url(#gapGrad)" barSize={20}
                  radius={[0, 6, 6, 0]} animationDuration={1200} animationBegin={500} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Risk Cards */}
      <div className="space-y-3">
        {risks.slice(0, 5).map((r, i) => {
          const color = RISK_LEVELS[Math.min(i, 2)];
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.08 }}
              className="nx-card p-5"
              style={{ borderLeft: `4px solid ${color}` }}
              data-testid={`risk-card-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" style={{ color }} />
                <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{r.title}</h3>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                {r.body.replace(/\*\*/g, '').split('\n').slice(0, 3).join(' ')}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function CareerPathsView({ content, meta }) {
  const [activePath, setActivePath] = useState(0);

  // Parse paths from content
  const pathBlocks = content.split(/##\s*Path\s*\d+:/gi);
  const paths = [];
  pathBlocks.slice(1).forEach((block, i) => {
    const lines = block.trim().split('\n').filter(l => l.trim());
    const title = lines[0]?.replace(/\*\*/g, '').trim() || `Path ${i + 1}`;
    const targetMatch = block.match(/Target Companies?:\*?\*?\s*([^\n]+)/i);
    const salaryMatch = block.match(/(?:AED|USD|\$)\s*[\d,]+(?:K)?(?:\s*[-–to]+\s*(?:AED|USD|\$)?\s*[\d,]+(?:K)?)?/gi);
    const body = lines.slice(1).join('\n').replace(/\*\*/g, '');

    paths.push({
      title: title.replace(/^\s*\(.*?\)\s*/, ''),
      targets: targetMatch ? targetMatch[1].trim() : '',
      salaries: salaryMatch || [],
      body,
    });
  });

  if (paths.length === 0) {
    content.split('\n\n').filter(p => p.trim() && !p.startsWith('#')).forEach((p, i) => {
      paths.push({ title: `Path ${i + 1}`, targets: '', salaries: [], body: p.replace(/\*\*/g, '') });
    });
  }

  const PATH_COLORS = ['#8B5CF6', '#0D9488', '#3B82F6', '#EC4899'];

  return (
    <div className="space-y-5">
      {/* Path Selector */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {paths.map((p, i) => {
          const color = PATH_COLORS[i % PATH_COLORS.length];
          const isActive = activePath === i;
          return (
            <button key={i} onClick={() => setActivePath(i)}
              className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all whitespace-nowrap flex-shrink-0"
              style={{
                background: isActive ? `${color}12` : 'var(--t-bg-section)',
                border: `2px solid ${isActive ? color : 'var(--t-border)'}`,
                boxShadow: isActive ? `0 4px 16px ${color}20` : 'none',
              }}
              data-testid={`path-btn-${i}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: isActive ? color : `${color}20` }}>
                <span className="text-sm font-black" style={{ color: isActive ? '#fff' : color }}>
                  {i + 1}
                </span>
              </div>
              <span className="text-xs font-bold" style={{ color: isActive ? color : 'var(--t-text-muted)' }}>
                {p.title.split('(')[0].trim().slice(0, 30)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active Path Detail */}
      <AnimatePresence mode="wait">
        {paths[activePath] && (() => {
          const p = paths[activePath];
          const color = PATH_COLORS[activePath % PATH_COLORS.length];
          return (
            <motion.div key={activePath}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              <div className="nx-card overflow-hidden" style={{ borderTop: `4px solid ${color}` }}>
                {/* Path header */}
                <div className="p-5" style={{ background: `${color}06` }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: color }}>
                      <Compass className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
                        {p.title}
                      </h3>
                      {p.targets && (
                        <p className="text-[10px] mt-0.5" style={{ color }}>
                          <Briefcase className="w-3 h-3 inline mr-1" />
                          {p.targets}
                        </p>
                      )}
                    </div>
                  </div>
                  {p.salaries.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {p.salaries.slice(0, 3).map((s, j) => (
                        <span key={j} className="text-xs font-bold px-3 py-1.5 rounded-full"
                          style={{ background: '#16A34A15', color: '#16A34A', border: '1px solid #16A34A25' }}>
                          <DollarSign className="w-3 h-3 inline mr-0.5" />{s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Path body */}
                <div className="p-5 space-y-3">
                  {p.body.split('\n').filter(l => l.trim()).map((line, j) => {
                    const isPoint = line.match(/^[-*]\s+/);
                    const clean = line.replace(/^[-*]\s+/, '').trim();
                    if (!clean) return null;
                    return (
                      <motion.div key={j} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + j * 0.03 }}>
                        {isPoint ? (
                          <div className="flex items-start gap-2.5 pl-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
                            <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{clean}</p>
                          </div>
                        ) : (
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{clean}</p>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}

function ActionPlanView({ content, meta }) {
  const [activePhase, setActivePhase] = useState(0);

  // Parse phases
  const phaseBlocks = content.split(/###\s*Phase\s*\d+:/gi);
  const phases = [];
  phaseBlocks.slice(1).forEach((block, i) => {
    const lines = block.trim().split('\n').filter(l => l.trim());
    const title = lines[0]?.replace(/\*\*/g, '').replace(/\(.*?\)/, '').trim() || `Phase ${i + 1}`;
    const timeMatch = lines[0]?.match(/\(([^)]+)\)/);
    const timeline = timeMatch ? timeMatch[1] : '';
    const body = lines.slice(1).join('\n');
    phases.push({ title, timeline, body });
  });

  if (phases.length === 0) {
    content.split('\n\n').filter(p => p.trim() && !p.startsWith('##')).forEach((p, i) => {
      phases.push({ title: `Phase ${i + 1}`, timeline: '', body: p.replace(/\*\*/g, '') });
    });
  }

  const PHASE_COLORS = ['#F5841F', '#0D9488', '#8B5CF6'];

  return (
    <div className="space-y-5">
      {/* Timeline Header */}
      <div className="nx-card p-5">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: meta.color }}>
          Your 90-Day Transformation Roadmap
        </p>
        <div className="relative flex items-center justify-between">
          {/* Connection line */}
          <div className="absolute left-0 right-0 top-1/2 h-1 rounded-full"
            style={{ background: 'var(--t-border)' }}>
            <motion.div initial={{ width: 0 }}
              animate={{ width: `${((activePhase + 1) / Math.max(phases.length, 1)) * 100}%` }}
              transition={{ duration: 0.5 }}
              className="h-full rounded-full" style={{ background: PHASE_COLORS[activePhase] || meta.color }} />
          </div>
          {phases.map((p, i) => {
            const color = PHASE_COLORS[i % PHASE_COLORS.length];
            const isDone = i < activePhase;
            const isActive = i === activePhase;
            return (
              <button key={i} onClick={() => setActivePhase(i)}
                className="relative z-10 flex flex-col items-center gap-2 cursor-pointer"
                data-testid={`phase-btn-${i}`}>
                <motion.div animate={{
                  scale: isActive ? 1.2 : 1,
                  boxShadow: isActive ? `0 0 20px ${color}40` : 'none'
                }}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                  style={{
                    background: isActive || isDone ? color : 'var(--t-bg-card)',
                    border: `3px solid ${isActive || isDone ? color : 'var(--t-border)'}`,
                  }}>
                  {isDone ? <CheckCircle className="w-5 h-5 text-white" /> :
                    <span className="text-sm font-black" style={{ color: isActive ? '#fff' : 'var(--t-text-muted)' }}>
                      {i + 1}
                    </span>}
                </motion.div>
                <div className="text-center">
                  <p className="text-[10px] font-bold" style={{ color: isActive ? color : 'var(--t-text-muted)' }}>
                    {p.title.slice(0, 15)}
                  </p>
                  {p.timeline && (
                    <p className="text-[8px]" style={{ color: 'var(--t-text-muted)' }}>{p.timeline}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Phase Detail */}
      <AnimatePresence mode="wait">
        {phases[activePhase] && (() => {
          const phase = phases[activePhase];
          const color = PHASE_COLORS[activePhase % PHASE_COLORS.length];
          const items = phase.body.split('\n').filter(l => l.trim());
          let weekBlocks = [];
          let current = null;

          items.forEach(line => {
            const weekMatch = line.match(/^\*?\*?Week\s*[\d-]+/i);
            if (weekMatch) {
              if (current) weekBlocks.push(current);
              current = { title: line.replace(/\*\*/g, '').trim(), items: [] };
            } else if (current) {
              current.items.push(line.replace(/^[-*]\s+/, '').replace(/\*\*/g, '').trim());
            } else {
              if (!current) current = { title: 'Tasks', items: [] };
              current.items.push(line.replace(/^[-*]\s+/, '').replace(/\*\*/g, '').trim());
            }
          });
          if (current) weekBlocks.push(current);

          return (
            <motion.div key={activePhase}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}
              className="space-y-3">
              {weekBlocks.map((wb, wi) => (
                <motion.div key={wi} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + wi * 0.1 }}
                  className="nx-card p-4" style={{ borderLeft: `3px solid ${color}` }}
                  data-testid={`week-block-${wi}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-3.5 h-3.5" style={{ color }} />
                    <span className="text-xs font-bold" style={{ color }}>{wb.title}</span>
                  </div>
                  <div className="space-y-2">
                    {wb.items.filter(t => t).map((task, ti) => (
                      <motion.div key={ti} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 + ti * 0.04 }}
                        className="flex items-start gap-2.5 px-2 py-1.5 rounded-lg"
                        style={{ background: 'var(--t-bg-section)' }}>
                        <div className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ border: `1.5px solid ${color}30`, background: `${color}08` }}>
                          <span className="text-[7px] font-bold" style={{ color }}>{ti + 1}</span>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{task}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
}

function MarketIntelView({ content, meta, marketDemand, marketLoading }) {
  // Parse key insights from content
  const lines = content.split('\n').filter(l => l.trim());
  const insights = [];
  lines.forEach(line => {
    const clean = line.replace(/\*\*/g, '').replace(/^[-*#]\s*/, '').trim();
    if (clean.length > 30) insights.push(clean);
  });

  const MARKET_COLORS = ['#3B82F6', '#0D9488', '#8B5CF6', '#F5841F', '#EC4899', '#16A34A'];
  const md = marketDemand || {};

  return (
    <div className="space-y-5">
      {/* Market Demand Hero Card */}
      {md.target_role && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="nx-card overflow-hidden" data-testid="market-demand-hero">
          <div className="p-5" style={{ background: `linear-gradient(135deg, ${meta.color}08, ${meta.color}15)` }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Demand Score Gauge */}
              <div className="text-center">
                <div className="relative w-20 h-20 mx-auto">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="38" fill="none" stroke="var(--t-border)" strokeWidth="7" />
                    <motion.circle cx="50" cy="50" r="38" fill="none"
                      stroke={md.demand_score >= 70 ? '#16A34A' : md.demand_score >= 40 ? '#F5841F' : '#DC2626'}
                      strokeWidth="7" strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 38}`}
                      initial={{ strokeDashoffset: 2 * Math.PI * 38 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 38 * (1 - (md.demand_score || 0) / 100) }}
                      transition={{ duration: 1.5, delay: 0.3 }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-black" style={{ color: meta.color }}>
                      <AnimatedCounter value={md.demand_score || 0} delay={300} />
                    </span>
                  </div>
                </div>
                <p className="text-[9px] font-bold mt-1 uppercase" style={{ color: 'var(--t-text-muted)' }}>Demand Score</p>
              </div>
              {/* Quick stats */}
              <div className="flex flex-col justify-center gap-2">
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Target Role</p>
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{md.target_role}</p>
                </div>
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Market Trend</p>
                  <p className="text-xs font-bold capitalize" style={{ color: md.demand_trend === 'growing' ? '#16A34A' : '#F5841F' }}>
                    <TrendingUp className="w-3 h-3 inline mr-1" />{md.demand_trend}
                  </p>
                </div>
              </div>
              {/* More stats */}
              <div className="flex flex-col justify-center gap-2">
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Avg. Time to Hire</p>
                  <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>
                    <AnimatedCounter value={md.time_to_hire_days || 0} delay={500} /> days
                  </p>
                </div>
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Remote %</p>
                  <p className="text-xs font-bold" style={{ color: '#8B5CF6' }}>
                    <AnimatedCounter value={md.remote_percentage || 0} suffix="%" delay={600} />
                  </p>
                </div>
              </div>
              {/* Competition */}
              <div className="flex flex-col justify-center gap-2">
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Competition</p>
                  <p className="text-xs font-bold capitalize" style={{
                    color: md.competition_level === 'low' ? '#16A34A' : md.competition_level === 'high' ? '#DC2626' : '#F5841F'
                  }}>{md.competition_level || 'N/A'}</p>
                </div>
                <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                  <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Geography</p>
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{md.target_geography || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Market Demand Trend Line Chart */}
      {md.role_demand_by_month?.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="market-demand-trend">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: meta.color }}>
            Role Demand Trend (Job Openings)
          </p>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={md.role_demand_by_month} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="demandGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: 'var(--t-text-muted)', fontSize: 10 }} />
                <YAxis tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }} formatter={(v) => [`${v} openings`, 'Job Openings']} />
                <Area type="monotone" dataKey="openings" stroke="#3B82F6"
                  fill="url(#demandGrad)" strokeWidth={2.5}
                  dot={{ fill: '#3B82F6', r: 5, stroke: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 7, strokeWidth: 3 }}
                  animationDuration={1500} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Salary Comparison Bar Chart */}
      {md.salary_comparison?.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="nx-card p-5" data-testid="market-salary-comparison">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#16A34A' }}>
            Salary Comparison
          </p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={md.salary_comparison} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <defs>
                  {md.salary_comparison.map((_, i) => (
                    <linearGradient key={i} id={`salGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={MARKET_COLORS[i % MARKET_COLORS.length]} stopOpacity={1} />
                      <stop offset="100%" stopColor={MARKET_COLORS[i % MARKET_COLORS.length]} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }}
                  tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }} formatter={(v) => [`AED ${v.toLocaleString()}`, 'Salary']} />
                <Bar dataKey="value" radius={[10, 10, 0, 0]} barSize={45}
                  animationDuration={1200} animationEasing="ease-out">
                  {md.salary_comparison.map((_, i) => (
                    <Cell key={i} fill={`url(#salGrad${i})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Top Skills Demand + Industry Growth Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Skills Demand */}
        {md.top_skills?.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="nx-card p-5" data-testid="market-skills-demand">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: '#8B5CF6' }}>
              Skills in Demand
            </p>
            <div className="space-y-3">
              {md.top_skills.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.08 }}
                  className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold w-20 truncate text-right" style={{ color: 'var(--t-text-muted)' }}>
                    {s.skill}
                  </span>
                  <div className="flex-1 h-5 rounded-md overflow-hidden relative" style={{ background: 'var(--t-border)' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${s.demand_pct}%` }}
                      transition={{ delay: 0.8 + i * 0.1, duration: 0.8 }}
                      className="h-full rounded-md relative overflow-hidden"
                      style={{ background: `linear-gradient(90deg, ${MARKET_COLORS[i % MARKET_COLORS.length]}bb, ${MARKET_COLORS[i % MARKET_COLORS.length]})` }}>
                      <div className="absolute inset-0 opacity-20"
                        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                          animation: 'shimmer 2.5s ease-in-out infinite' }} />
                    </motion.div>
                  </div>
                  <span className="text-[10px] font-black w-10 text-right" style={{ color: MARKET_COLORS[i % MARKET_COLORS.length] }}>
                    {s.demand_pct}%
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Industry Growth */}
        {md.industry_growth?.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="nx-card p-5" data-testid="market-industry-growth">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#F5841F' }}>
              Industry Growth
            </p>
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={md.industry_growth} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="growthGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#F5841F" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#16A34A" stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }}
                    tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="industry" width={65}
                    tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <Tooltip contentStyle={{
                    background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                    borderRadius: 10, fontSize: 11,
                  }} formatter={(v) => [`${v}%`, 'Growth Rate']} />
                  <Bar dataKey="growth_pct" fill="url(#growthGrad)" barSize={18}
                    radius={[0, 8, 8, 0]} animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}
      </div>

      {/* Key Insight Callout */}
      {md.key_insight && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="nx-card p-5 relative overflow-hidden" data-testid="market-key-insight"
          style={{ borderLeft: `4px solid ${meta.color}` }}>
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full"
            style={{ background: `${meta.color}05`, transform: 'translate(30%, -30%)' }} />
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4" style={{ color: meta.color }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
              Key Market Insight
            </span>
          </div>
          <p className="text-sm leading-relaxed font-medium" style={{ color: 'var(--t-text-heading)' }}>
            {md.key_insight}
          </p>
        </motion.div>
      )}

      {/* Loading state for market demand */}
      {marketLoading && (
        <div className="nx-card p-8 flex flex-col items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin mb-2" style={{ color: meta.color }} />
          <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Analyzing market demand data...</p>
        </div>
      )}

      {/* Text Insights from LLM content */}
      <div className="space-y-3">
        {insights.filter(ins => ins.length > 30).slice(0, 6).map((insight, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 + i * 0.06 }}
            className="nx-card p-4 flex items-start gap-3"
            data-testid={`market-insight-${i}`}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${MARKET_COLORS[i % MARKET_COLORS.length]}12` }}>
              <TrendingUp className="w-4 h-4" style={{ color: MARKET_COLORS[i % MARKET_COLORS.length] }} />
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{insight}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}


// ===========================================================
// Sub-view: Peer Benchmark (You vs Industry Peers)
// ===========================================================
function PeerBenchmarkView({ benchmark, loading, meta, grades }) {
  if (loading) return (
    <div className="nx-card p-10 flex flex-col items-center justify-center">
      <Loader2 className="w-7 h-7 animate-spin mb-3" style={{ color: meta.color }} />
      <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>Analyzing peer data...</p>
      <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>Comparing your scores against industry peers</p>
    </div>
  );
  if (!benchmark) return <EmptyState text="Complete more assessments to unlock peer benchmark comparison." />;

  const dims = benchmark.dimensions || [];
  const peerGroup = benchmark.peer_group || 'Industry Peers';
  const sampleSize = benchmark.sample_size || 0;
  const overallPct = benchmark.overall_percentile || 0;

  // Radar data: user vs peer avg
  const radarData = dims.map(d => ({
    dimension: d.name.split(' ').slice(0, 2).join(' '),
    You: d.user_score,
    Peers: d.peer_avg,
    'Top 10%': d.peer_top_10,
    fullMark: 10,
  }));

  // Comparison bars data
  const barData = dims.map(d => ({
    name: d.name.split(' ')[0],
    user: d.user_score,
    peers: d.peer_avg,
    top10: d.peer_top_10,
  }));

  const aboveCount = benchmark.above_average_count || 0;
  const belowCount = benchmark.below_average_count || 0;

  return (
    <div className="space-y-5">
      {/* Hero Percentile Card */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="nx-card overflow-hidden" data-testid="peer-benchmark-hero">
        <div className="p-6" style={{ background: `linear-gradient(135deg, ${meta.color}08, ${meta.color}18)` }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
            {/* Overall Percentile Gauge */}
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--t-border)" strokeWidth="7" />
                  <motion.circle cx="50" cy="50" r="40" fill="none"
                    stroke={meta.color} strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 40 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 40 * (1 - overallPct / 100) }}
                    transition={{ duration: 1.5, delay: 0.3 }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-black" style={{ color: meta.color }}>
                    <AnimatedCounter value={overallPct} delay={300} />
                  </span>
                  <span className="text-[8px] font-bold" style={{ color: 'var(--t-text-muted)' }}>PERCENTILE</span>
                </div>
              </div>
              <p className="text-[9px] font-bold mt-1 uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>
                Your Rank
              </p>
            </div>

            {/* Stats */}
            <div className="space-y-2">
              <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Peer Group</p>
                <p className="text-[10px] font-bold" style={{ color: 'var(--t-text-heading)' }}>{peerGroup}</p>
              </div>
              <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Sample Size</p>
                <p className="text-[10px] font-bold" style={{ color: meta.color }}>
                  <AnimatedCounter value={sampleSize} delay={400} /> professionals
                </p>
              </div>
            </div>

            {/* Above/Below */}
            <div className="space-y-2">
              <div className="px-3 py-2 rounded-lg" style={{ background: '#16A34A08', border: '1px solid #16A34A20' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Above Average</p>
                <p className="text-xl font-black" style={{ color: '#16A34A' }}>{aboveCount}</p>
              </div>
              <div className="px-3 py-2 rounded-lg" style={{ background: '#DC262608', border: '1px solid #DC262620' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: 'var(--t-text-muted)' }}>Below Average</p>
                <p className="text-xl font-black" style={{ color: '#DC2626' }}>{belowCount}</p>
              </div>
            </div>

            {/* Standout Dims */}
            <div className="space-y-2">
              <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: '#16A34A' }}>Standout</p>
                {(benchmark.standout_dimensions || []).slice(0, 2).map((d, i) => (
                  <p key={i} className="text-[10px] font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{d}</p>
                ))}
              </div>
              <div className="px-3 py-2 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                <p className="text-[9px] uppercase font-bold" style={{ color: '#DC2626' }}>Gaps</p>
                {(benchmark.gap_dimensions || []).slice(0, 2).map((d, i) => (
                  <p key={i} className="text-[10px] font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{d}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* You vs Peers Radar Chart */}
      {radarData.length >= 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="peer-radar-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: meta.color }}>
            You vs Peers (Radar)
          </p>
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                <PolarGrid stroke="var(--t-border)" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
                <Radar name="You" dataKey="You" stroke={meta.color} fill={meta.color}
                  fillOpacity={0.25} strokeWidth={2.5} animationDuration={1500} />
                <Radar name="Peers" dataKey="Peers" stroke="#6B728088" fill="#6B728010"
                  fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="5 5" animationDuration={1000} />
                <Radar name="Top 10%" dataKey="Top 10%" stroke="#16A34A" fill="none"
                  strokeWidth={1} strokeDasharray="3 3" animationDuration={1200} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Dimension-by-Dimension Comparison */}
      {dims.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="nx-card p-5" data-testid="peer-dimension-comparison">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: meta.color }}>
            Dimension-by-Dimension Comparison
          </p>
          <div className="space-y-3">
            {dims.sort((a, b) => (b.user_score - b.peer_avg) - (a.user_score - a.peer_avg)).map((d, i) => {
              const diff = d.user_score - d.peer_avg;
              const isAbove = diff > 0;
              const color = isAbove ? '#16A34A' : '#DC2626';
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.04 }}
                  className="flex items-center gap-3 group">
                  <span className="text-[10px] font-semibold w-24 truncate text-right" style={{ color: 'var(--t-text-muted)' }}>
                    {d.name}
                  </span>
                  {/* Your score bar */}
                  <div className="flex-1 relative h-5 rounded-md" style={{ background: 'var(--t-border)' }}>
                    {/* Peer avg marker */}
                    <div className="absolute top-0 h-full w-0.5" style={{
                      left: `${(d.peer_avg / 10) * 100}%`,
                      background: '#6B728088',
                    }} />
                    {/* Your bar */}
                    <motion.div initial={{ width: 0 }}
                      animate={{ width: `${(d.user_score / 10) * 100}%` }}
                      transition={{ delay: 0.7 + i * 0.05, duration: 0.8 }}
                      className="h-full rounded-md relative"
                      style={{ background: `${meta.color}cc` }}>
                    </motion.div>
                    <span className="absolute right-1.5 top-0.5 text-[9px] font-bold" style={{ color: 'var(--t-text-heading)' }}>
                      {d.user_score}
                    </span>
                  </div>
                  {/* Delta badge */}
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ delay: 0.9 + i * 0.04, type: 'spring' }}
                    className="text-[10px] font-black px-2 py-0.5 rounded-full w-14 text-center"
                    style={{ background: `${color}12`, color }}>
                    {isAbove ? '+' : ''}{diff.toFixed(1)}
                  </motion.span>
                  {/* Percentile */}
                  <span className="text-[9px] font-bold w-8 text-right" style={{ color: 'var(--t-text-muted)' }}>
                    P{d.percentile}
                  </span>
                </motion.div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid var(--t-border)' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-2 rounded-sm" style={{ background: `${meta.color}cc` }} />
              <span className="text-[9px] font-bold" style={{ color: 'var(--t-text-muted)' }}>Your Score</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-0.5 h-4" style={{ background: '#6B728088' }} />
              <span className="text-[9px] font-bold" style={{ color: 'var(--t-text-muted)' }}>Peer Average</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Insight Card */}
      {benchmark.insight && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="nx-card p-5 relative overflow-hidden"
          style={{ borderLeft: `4px solid ${meta.color}` }}
          data-testid="peer-insight">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full"
            style={{ background: `${meta.color}05`, transform: 'translate(30%, -30%)' }} />
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4" style={{ color: meta.color }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
              Peer Comparison Insight
            </span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-heading)' }}>
            {benchmark.insight}
          </p>
        </motion.div>
      )}
    </div>
  );
}


// ===========================================================
// Tab: Expert Panel (Interactive, Fun)
// ===========================================================
function PanelTab({ data, loading }) {
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activeAdvisor, setActiveAdvisor] = useState(null);
  const panelRef = useRef(null);

  if (loading) return <LoadingState text="Loading advisory panel..." />;
  if (!data) return <EmptyState text="Start your discovery to see your advisory panel." />;

  const basePanel = data.base_panel || [];
  const evolvedPanel = data.evolved_panel || [];
  const allMembers = [...evolvedPanel, ...basePanel];
  const isEvolved = data.evolved;

  const loadInsights = async () => {
    if (insights) return;
    setInsightsLoading(true);
    try {
      const res = await kaireraAPI.getPanelInsights();
      setInsights(res.data.insights || []);
    } catch (err) {
      toast.error('Failed to load advisor insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  const SLOT_COLORS = [
    '#0D9488', '#8B5CF6', '#EC4899', '#F5841F', '#3B82F6', '#16A34A',
    '#DC2626', '#EAB308', '#6366F1', '#14B8A6', '#F97316', '#A855F7',
  ];

  return (
    <div className="space-y-6" data-testid="panel-tab" ref={panelRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        {isEvolved && (
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: '#0D9488' }} />
            <span className="text-xs font-bold" style={{ color: '#0D9488' }}>
              Panel evolved with {evolvedPanel.length} specialist{evolvedPanel.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className="flex gap-2 ml-auto">
          {!insights && (
            <Button onClick={loadInsights} disabled={insightsLoading}
              className="h-8 px-3 text-xs rounded-lg gap-1.5"
              style={{ background: '#0D9488', color: '#fff' }}
              data-testid="load-insights-btn">
              {insightsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
              {insightsLoading ? 'Loading...' : 'Hear From Your Panel'}
            </Button>
          )}
          <Button onClick={() => shareAsImage(panelRef, 'expert-panel')}
            className="h-8 px-3 text-xs rounded-lg gap-1.5"
            style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
            data-testid="download-panel-btn">
            <Download className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Advisor Cards */}
      <div className="grid gap-4">
        {allMembers.map((member, i) => {
          const slotColor = SLOT_COLORS[i % SLOT_COLORS.length];
          const isEvo = i < evolvedPanel.length;
          const slotNum = member.slot_number || member.slot || i + 1;
          const archetype = member.expert_archetype || member.archetype;
          const insight = insights?.find(ins => ins.slot === slotNum || ins.archetype === archetype);
          const isActive = activeAdvisor === i;

          return (
            <motion.div key={i} {...fadeIn(i * 0.05)}
              className="nx-card overflow-hidden"
              style={{ borderLeft: `4px solid ${slotColor}` }}
              data-testid={`advisor-${i}`}>
              <button onClick={() => { setActiveAdvisor(isActive ? null : i); if (!insights) loadInsights(); }}
                className="w-full flex items-center gap-4 p-4 text-left transition-all"
                style={{ background: isActive ? `${slotColor}06` : 'transparent' }}>
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{ background: `${slotColor}12` }}>
                    <span className="text-lg font-black" style={{ color: slotColor }}>
                      {archetype?.charAt(0) || '?'}
                    </span>
                  </div>
                  {isEvo && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: '#0D9488' }}>
                      <Sparkles className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>
                      {archetype}
                    </h3>
                    <span className="text-[8px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ background: `${slotColor}10`, color: slotColor }}>
                      #{slotNum}
                    </span>
                  </div>
                  <p className="text-[11px] truncate" style={{ color: 'var(--t-text-muted)' }}>
                    {member.specialty}
                  </p>
                  {member.rationale && !isActive && (
                    <p className="text-[10px] mt-1 truncate" style={{ color: slotColor }}>
                      {member.rationale}
                    </p>
                  )}
                </div>

                <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isActive ? 'rotate-180' : ''}`}
                  style={{ color: 'var(--t-text-muted)' }} />
              </button>

              {/* Expanded Insight */}
              <AnimatePresence>
                {isActive && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                    className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--t-border)' }}>
                      {member.rationale && (
                        <div className="mt-3 p-3 rounded-xl" style={{ background: `${slotColor}06`, border: `1px solid ${slotColor}15` }}>
                          <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: slotColor }}>
                            Why This Advisor
                          </p>
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                            {member.rationale}
                          </p>
                        </div>
                      )}

                      {insightsLoading && !insight && (
                        <div className="flex items-center gap-2 py-3">
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: slotColor }} />
                          <span className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                            Generating personalized insight...
                          </span>
                        </div>
                      )}

                      {insight && (
                        <>
                          {/* Observation - Speech Bubble */}
                          <div className="relative mt-3">
                            <div className="p-4 rounded-xl" style={{ background: `${slotColor}08`, border: `1px solid ${slotColor}20` }}>
                              <div className="flex items-center gap-2 mb-2">
                                <Eye className="w-3.5 h-3.5" style={{ color: slotColor }} />
                                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: slotColor }}>
                                  What I See
                                </span>
                              </div>
                              <p className="text-xs leading-relaxed italic" style={{ color: 'var(--t-text-secondary)' }}>
                                "{insight.observation}"
                              </p>
                            </div>
                          </div>

                          {/* Recommendation */}
                          <div className="p-4 rounded-xl" style={{ background: 'rgba(22,163,74,0.04)', border: '1px solid rgba(22,163,74,0.15)' }}>
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="w-3.5 h-3.5" style={{ color: '#16A34A' }} />
                              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#16A34A' }}>
                                My Recommendation
                              </span>
                            </div>
                            <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                              {insight.recommendation}
                            </p>
                          </div>

                          {/* Risk Flag */}
                          {insight.risk_flag && (
                            <div className="p-3 rounded-xl" style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)' }}>
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3" style={{ color: '#DC2626' }} />
                                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#DC2626' }}>
                                  Watch Out
                                </span>
                              </div>
                              <p className="text-[11px] mt-1 leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
                                {insight.risk_flag}
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {!insightsLoading && !insight && insights && (
                        <p className="text-xs py-2" style={{ color: 'var(--t-text-muted)' }}>
                          No specific insight available. Try refreshing your panel.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Methodology */}
      <div className="rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
          <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: '#0D9488' }}>
            How Your Panel Was Assembled
          </span>
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
          Your advisory panel starts with 10 core advisors covering the essential career dimensions.
          As you complete assessment batteries, specialized advisors are added based on your unique
          strengths and development areas. Each advisor provides perspective through the lens of their
          expertise, ensuring you receive well-rounded career guidance.
        </p>
      </div>
    </div>
  );
}

// ===========================================================
// Tab: Devil's Advocate (unchanged)
// ===========================================================
function DevilsAdvocateTab({ navigate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await kaireraAPI.devilsAdvocate(null);
        setMessages(res.data.messages || []);
      } catch (err) {
        console.error('DA init failed:', err);
      } finally {
        setInitializing(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await kaireraAPI.devilsAdvocate(msg);
      setMessages(res.data.messages || []);
    } catch (err) { console.error('DA send failed:', err); }
    finally { setLoading(false); }
  };

  const handleReset = async () => {
    await kaireraAPI.resetDevilsAdvocate();
    setMessages([]);
    setInitializing(true);
    try {
      const res = await kaireraAPI.devilsAdvocate(null);
      setMessages(res.data.messages || []);
    } catch (err) { console.error(err); }
    finally { setInitializing(false); }
  };

  if (initializing) return <LoadingState text="Preparing Devil's Advocate..." />;

  return (
    <div className="flex flex-col" style={{ minHeight: '50vh' }} data-testid="devils-advocate-tab">
      <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.15)' }}>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4" style={{ color: '#DC2626' }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#DC2626' }}>Stress-Test Mode</span>
        </div>
        <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
          This protocol challenges your career assumptions and identifies blind spots. Be prepared for tough questions.
        </p>
      </div>

      <div className="flex-1 space-y-3 mb-4 overflow-y-auto" style={{ maxHeight: 400 }}>
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[85%] px-4 py-3 rounded-xl text-sm leading-relaxed"
              style={{
                background: m.role === 'user' ? '#0D9488' : 'var(--t-bg-section)',
                color: m.role === 'user' ? '#fff' : 'var(--t-text)',
                border: m.role === 'user' ? 'none' : '1px solid var(--t-border)',
              }}>
              {m.role !== 'user' && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Shield className="w-3 h-3" style={{ color: '#DC2626' }} />
                  <span className="text-[9px] font-bold uppercase" style={{ color: '#DC2626' }}>Devil's Advocate</span>
                </div>
              )}
              {m.content}
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#DC2626' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2">
        <button onClick={handleReset} className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}
          data-testid="da-reset-btn">
          <RotateCcw className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
        </button>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Defend your career plan..."
          className="flex-1 h-10 px-4 rounded-lg text-sm"
          style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
          data-testid="da-input" />
        <Button onClick={handleSend} disabled={!input.trim() || loading}
          className="h-10 px-4 rounded-lg" style={{ background: '#0D9488', color: '#fff' }}
          data-testid="da-send-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ===========================================================
// Shared Components
// ===========================================================
function LoadingState({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: '#0D9488' }} />
      <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{text}</p>
    </div>
  );
}
function EmptyState({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Lock className="w-10 h-10 mb-4 opacity-30" style={{ color: '#0D9488' }} />
      <p className="text-sm text-center max-w-xs" style={{ color: 'var(--t-text-muted)' }}>{text}</p>
    </div>
  );
}

// ===========================================================
// MAIN PAGE
// ===========================================================
export default function KaireraReportPage() {
  const [activeTab, setActiveTab] = useState('report-card');
  const [reportCard, setReportCard] = useState(null);
  const [persona, setPersona] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [panelData, setPanelData] = useState(null);
  const [loading, setLoading] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const loadTab = async (tab) => {
      setLoading(prev => ({ ...prev, [tab]: true }));
      try {
        if (tab === 'report-card') {
          const res = await kaireraAPI.getReportCard();
          setReportCard(res.data);
        } else if (tab === 'persona') {
          const res = await kaireraAPI.getPersona();
          setPersona(res.data);
        } else if (tab === 'analysis') {
          // Load report card data too if not already loaded (needed for grade charts)
          if (!reportCard) {
            kaireraAPI.getReportCard().then(r => setReportCard(r.data)).catch(() => {});
          }
          const res = await kaireraAPI.getCareerReport();
          setAnalysis(res.data);
        } else if (tab === 'panel') {
          const res = await kaireraAPI.getPanel();
          setPanelData(res.data);
        }
      } catch (err) {
        console.error(`Failed to load ${tab}:`, err);
      } finally {
        setLoading(prev => ({ ...prev, [tab]: false }));
      }
    };
    loadTab(activeTab);
  }, [activeTab]);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
          Career Intelligence
        </p>
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
          Intelligence Report
        </h1>
      </div>

      {/* Tab Nav */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl overflow-x-auto" style={{ background: 'var(--t-bg-section)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
              style={{
                background: isActive ? 'var(--t-bg-card)' : 'transparent',
                color: isActive ? '#0D9488' : 'var(--t-text-muted)',
                boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
              data-testid={`tab-${tab.id}`}>
              <Icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}>
          {activeTab === 'report-card' && <ReportCardTab data={reportCard} loading={loading['report-card']} />}
          {activeTab === 'persona' && <PersonaTab data={persona} loading={loading.persona} />}
          {activeTab === 'analysis' && <AnalysisTab data={analysis} loading={loading.analysis} reportCardData={reportCard} />}
          {activeTab === 'panel' && <PanelTab data={panelData} loading={loading.panel} />}
          {activeTab === 'devils-advocate' && <DevilsAdvocateTab navigate={navigate} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
